import AccessTimeIcon from '@mui/icons-material/AccessTime';
import AddTaskIcon from '@mui/icons-material/AddTask';
import ArrowRightAltIcon from '@mui/icons-material/ArrowRightAlt';
import AssignmentIcon from '@mui/icons-material/Assignment';
import BoltIcon from '@mui/icons-material/Bolt';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DiamondIcon from '@mui/icons-material/Diamond';
import EditIcon from '@mui/icons-material/Edit';
import EventIcon from '@mui/icons-material/Event';
import EventRepeatIcon from '@mui/icons-material/EventRepeat';
import FlagIcon from '@mui/icons-material/Flag';
import GroupsIcon from '@mui/icons-material/Groups';
import HistoryIcon from '@mui/icons-material/History';
import LinkIcon from '@mui/icons-material/Link';
import NotesIcon from '@mui/icons-material/Notes';
import PersonIcon from '@mui/icons-material/Person';
import RuleIcon from '@mui/icons-material/Rule';
import TaskIcon from '@mui/icons-material/Task';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import type {
  IChangeRequestRef,
  IIssue,
  IssueDependencyType,
  IssueLinkType,
  IssuePriority,
  IssueType,
  IIssueLink,
} from '@pulse/shared';
import {
  Alert,
  Box,
  Button,
  ButtonBase,
  Checkbox,
  Chip,
  CircularProgress,
  CustomSnackbar,
  DateField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  ICON_SIZE,
  IconButton,
  InputLabel,
  LabelWithIcon,
  Link as MuiLink,
  MarkdownField,
  MenuItem,
  Select,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
  UserAvatar,
  UserAvatarOverflow,
  useIsMobile,
} from '@pulse/ui';
import { useEffect, useRef, useState, type ReactElement, type ReactNode } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import { CopyLinkButton } from '../../../components/CopyLinkButton';
import { CompactEntityPicker } from '../../../components/EntityPicker';
import { MultiSelectPicker } from '../../../components/MultiSelectPicker';
import { RemoveConfirmDialog } from '../../../components/RemoveConfirmDialog';
import { ScrollableBox } from '../../../components/ScrollableText';
import { LINE_CLAMP_2_SX, TruncatableText } from '../../../components/TruncatableText';
import { useAuth } from '../../../context/AuthContext';
import { formatDdMmYyyy, formatDdMmYyyyHm } from '../../../lib/date.util';
import {
  blockNegativeHourKeys,
  cappedHoursChange,
  isValidHours,
} from '../../../lib/hours-input.util';
import { isEditableWorkLogDate } from '../../../lib/work-log-edit-window.util';
import { useUserLookup } from '../../access-management/hooks/useUsers';
import type { IssueInput } from '../api/issue.api';
import { useChangeRequestLookup } from '../hooks/useChangeRequests';
import {
  useAddIssueComment,
  useAddIssueLink,
  useAddIssueWorkLog,
  useActivateIssue,
  useDeactivateIssue,
  useIssue,
  useIssueComments,
  useIssues,
  useIssueWorkLogs,
  useRemoveIssueLink,
  useToggleFollowIssue,
  useTransitionIssue,
  useUpdateIssue,
  useUpdateIssueComment,
  useUpdateIssueWorkLog,
} from '../hooks/useIssues';
import { useMilestones } from '../hooks/useMilestones';
import { useProjectModules } from '../hooks/useProjectModules';
import { useSprints } from '../hooks/useSprints';
import { useWorkflowDefinition } from '../hooks/useWorkflow';
import { issueLink } from '../utils/entityLinks';
import { checkStartDateAgainstDependencies } from '../utils/issueDependency';
import { dateBoundError, dateInputBounds, parentDateBounds, subIssueDateBounds } from '../utils/issueHierarchyDates';
import {
  DEPENDENCY_TYPES,
  DEPENDENCY_TYPE_LABELS,
  formatIssueLinkType,
} from '../utils/issueLinkType';

import { InlineEditCell, InlineMenuCell } from './InlineEditControls';
import { IssueFormDialog } from './IssueFormDialog';
import { IssueHistoryPanel } from './IssueHistoryPanel';

// A CR that's left Approved is no longer offered by the lookup, but an issue already tagged to
// it (issue.changeRequest) still carries it, so merge it back in — same rule as
// IssueLinkPage.tsx's withSelected() for its own Change Request field.
function withSelected(options: IChangeRequestRef[], selected?: IChangeRequestRef | null): IChangeRequestRef[] {
  return selected && !options.some((o) => o.id === selected.id) ? [...options, selected] : options;
}

const LINK_TYPES: IssueLinkType[] = ['Blocked by', 'Depends on', 'Duplicate of', 'Related to'];
const ISSUE_TYPES: IssueType[] = ['Story', 'Defect', 'Task'];
const ISSUE_PRIORITIES: IssuePriority[] = ['Critical', 'High', 'Medium', 'Low'];
const TABS = ['Details', 'Sub-Issues', 'Links', 'Comments', 'Work Log', 'History'] as const;
// Same icons as the matching section headers on IssueLinkPage.tsx's standalone permalink page,
// so a tab here and its counterpart section there read as the same concept.
const TAB_ICON: Record<(typeof TABS)[number], ReactElement> = {
  Details: <AssignmentIcon fontSize="small" />,
  'Sub-Issues': <AddTaskIcon fontSize="small" />,
  Links: <LinkIcon fontSize="small" />,
  Comments: <ChatBubbleOutlineIcon fontSize="small" />,
  'Work Log': <AccessTimeIcon fontSize="small" />,
  History: <HistoryIcon fontSize="small" />,
};
// Matches CreateIssueDto's @MaxLength(6000) on description (apps/api/src/modules/presales/dto/create-issue.dto.ts).
const DESCRIPTION_MAX = 6000;
// Matches CreateIssueCommentDto's @MaxLength(20000) on body (apps/api/src/modules/presales/dto/create-issue-comment.dto.ts).
const COMMENT_MAX = 20000;
// Matches CreateIssueWorkLogDto's @MaxLength(5000) on description (apps/api/src/modules/presales/dto/create-issue-work-log.dto.ts).
const WORK_LOG_DESCRIPTION_MAX = 5000;
const DESCRIPTION_HEIGHT = 150;
const DESCRIPTION_ROWS = 3;

export function IssueDetailDialog({
  projectId,
  issueId,
  onClose,
  initialTab = 'Details',
}: {
  projectId: string;
  issueId: string;
  onClose: () => void;
  /** Opens the dialog straight to a given tab — e.g. Home's row-level "Comments" action jumps
   *  straight to Comments instead of always landing on Details first. */
  initialTab?: (typeof TABS)[number];
}) {
  // Clicking a sub-issue's key "flips" this same dialog to show the sub-issue instead of
  // stacking a new dialog on top — every fetch/mutation below keys off this, not the `issueId`
  // prop, so the dialog always reflects whichever issue is currently being viewed.
  const [currentIssueId, setCurrentIssueId] = useState(issueId);
  const { data: issue, isLoading, isError } = useIssue(projectId, currentIssueId);
  const { data: allIssues = [] } = useIssues(projectId);
  const { data: users = [] } = useUserLookup();
  const { user } = useAuth();
  // Matches IssueFormDialog's own create-time gating — Milestone trigger stays
  // project_finance-only, whether setting it at creation or editing it afterward.
  const canSeeMilestones = user?.allScopePermissions.includes('project_finance:read') ?? false;
  // project_finance:read only opens the Milestone trigger row for viewing — changing it needs
  // project_finance:write, same read-vs-write split the Milestone endpoints themselves use
  // (milestone.controller.ts: read on GET, write on every mutation).
  const canEditMilestones = user?.allScopePermissions.includes('project_finance:write') ?? false;
  const { data: milestones = [] } = useMilestones(projectId, canSeeMilestones);
  const { data: modules = [] } = useProjectModules(projectId);
  const { data: sprints = [] } = useSprints(projectId);
  // Approved-only, filtered by the API against the configured step (CHANGE_REQUEST_APPROVAL_STEP)
  // — nothing here compares approvalStatus itself. withSelected() keeps this issue's own CR
  // offered even once it has left Approved.
  const { data: approvedChangeRequests = [] } = useChangeRequestLookup(projectId);
  // A deactivated/closed Module or Sprint isn't offered for anything new, but an issue already
  // on one still displays its name via issue.module/issue.sprint — same convention as the
  // Planner (ProjectPlannerTab.tsx) and Create Issue (IssueFormDialog.tsx).
  const assignableModules = modules.filter((m) => m.isActive);
  const assignableSprints = sprints.filter((s) => s.isActive && !s.isClosed);
  // CompactEntityPicker's option label always reads `.name`, so the crNumber prefix is folded
  // into a synthetic display name here rather than left for a custom renderOption.
  const changeRequestPickerOptions = withSelected(approvedChangeRequests, issue?.changeRequest ?? null).map(
    (cr) => ({ id: cr.id, name: `${cr.crNumber} — ${cr.name}` }),
  );
  const selectedChangeRequest = issue?.changeRequest
    ? { id: issue.changeRequest.id, name: `${issue.changeRequest.crNumber} — ${issue.changeRequest.name}` }
    : null;
  const isMobile = useIsMobile();
  const [tab, setTab] = useState<(typeof TABS)[number]>(initialTab);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [subIssueOpen, setSubIssueOpen] = useState(false);
  const [pendingDoneStep, setPendingDoneStep] = useState<string | null>(null);
  const [editingDescription, setEditingDescription] = useState(false);
  const [descriptionDraft, setDescriptionDraft] = useState('');
  const descriptionOverLimit = descriptionDraft.length > DESCRIPTION_MAX;

  const prevIssueIdRef = useRef(currentIssueId);
  useEffect(() => {
    if (prevIssueIdRef.current === currentIssueId) return;
    prevIssueIdRef.current = currentIssueId;
    setTab('Details');
    setEditingDescription(false);
    setDescriptionDraft('');
  }, [currentIssueId]);
  const selectedTabRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    let rafId: number;
    const deadline = performance.now() + 400; // generous vs. the ~17ms gap actually observed

    function correct() {
      const el = selectedTabRef.current;
      const scroller = el?.closest('.MuiTabs-scroller');
      if (el && scroller instanceof HTMLElement) {
        const elLeftInContent = el.getBoundingClientRect().left - scroller.getBoundingClientRect().left + scroller.scrollLeft;
        const target = Math.max(0, elLeftInContent - (scroller.clientWidth - el.clientWidth) / 2);
        if (Math.abs(scroller.scrollLeft - target) > 0.5) scroller.scrollLeft = target;
      }
      if (performance.now() < deadline) rafId = requestAnimationFrame(correct);
    }
    rafId = requestAnimationFrame(correct);
    return () => cancelAnimationFrame(rafId);
  }, [tab, issue]);
  
  const { data: workflow } = useWorkflowDefinition('issue');
  const transitionMutation = useTransitionIssue();
  const updateMutation = useUpdateIssue();
  const followMutation = useToggleFollowIssue();
  const deactivateMutation = useDeactivateIssue();
  const activateMutation = useActivateIssue();
  const [confirmDeactivateOpen, setConfirmDeactivateOpen] = useState(false);

  // issue:deactivate is held by Tech Lead and Project Manager roles.
  const canDeactivate = Boolean(user?.permissions.includes('issue:deactivate'));

  const [linkTargets, setLinkTargets] = useState<IIssue[]>([]);
  const [linkType, setLinkType] = useState<IssueLinkType>('Related to');
  const [dependencyType, setDependencyType] = useState<IssueDependencyType>('FS');
  const addLinkMutation = useAddIssueLink();
  const removeLinkMutation = useRemoveIssueLink();
  const [linkToRemove, setLinkToRemove] = useState<IIssueLink | null>(null);

  async function handleAddLinks() {
    try {
      for (const t of linkTargets) {
        await addLinkMutation.mutateAsync({
          projectId,
          issueId: currentIssueId,
          linkedIssueId: t.id,
          linkType,
          dependencyType: linkType === 'Depends on' ? dependencyType : undefined,
        });
      }
      setLinkTargets([]);
    } catch (err) {
      setErrorMessage((err as Error).message);
    }
  }

  const { data: comments = [] } = useIssueComments(projectId, currentIssueId);
  const [commentBody, setCommentBody] = useState('');
  const commentOverLimit = commentBody.length > COMMENT_MAX;
  const addCommentMutation = useAddIssueComment();
  const updateCommentMutation = useUpdateIssueComment();

  const { data: workLogs = [] } = useIssueWorkLogs(projectId, currentIssueId);
  const [logHours, setLogHours] = useState('');
  // Defaults to today but stays editable — logging a prior day's hours (back-dated entries) is
  // a normal Work Log use case, not just same-day entry.
  const [logDate, setLogDate] = useState(() => new Date().toLocaleDateString('en-CA'));
  const [logDateFieldValid, setLogDateFieldValid] = useState(true);
  const [logDescription, setLogDescription] = useState('');
  const logDescriptionOverLimit = logDescription.length > WORK_LOG_DESCRIPTION_MAX;
  // The 24h-per-day budget spans the user's WHOLE day (across every issue/dialog it can be
  // logged from), which this component has no reliable way to know in advance — enforced
  // server-side instead (DailyHoursWithinLimit in IssueService).
  const addWorkLogMutation = useAddIssueWorkLog();
  const updateWorkLogMutation = useUpdateIssueWorkLog();
  const [editingWorkLogId, setEditingWorkLogId] = useState<string | null>(null);
  const [editWorkLogHours, setEditWorkLogHours] = useState('');
  const [editWorkLogDescription, setEditWorkLogDescription] = useState('');
  const editWorkLogDescriptionOverLimit = editWorkLogDescription.length > WORK_LOG_DESCRIPTION_MAX;

  if (isLoading) {
    return (
      <Dialog open onClose={onClose} fullWidth maxWidth="md">
        <DialogContent>
          <CircularProgress />
        </DialogContent>
      </Dialog>
    );
  }
  if (isError || !issue) {
    return (
      <Dialog open onClose={onClose} fullWidth maxWidth="md">
        <DialogContent>
          <Alert severity="error">Failed to load this issue.</Alert>
        </DialogContent>
      </Dialog>
    );
  }

  // §15 — an issue may trigger a Payment milestone, a Funding milestone, or both independently;
  // this confirmation must describe whichever of the two are actually set.
  const triggeredMilestoneLabels = [
    issue.paymentMilestone ? `a Payment milestone ("${issue.paymentMilestone.name}")` : null,
    issue.fundingMilestone ? `a Funding milestone ("${issue.fundingMilestone.name}")` : null,
  ].filter((label): label is string => label !== null);

  const subIssues = allIssues.filter((i) => i.parentIssueId === issue.id);
  const visibleTabs = issue.parentIssueId ? TABS.filter((t) => t !== 'Sub-Issues') : TABS;
  const parentIssue = issue.parentIssueId ? allIssues.find((i) => i.id === issue.parentIssueId) : undefined;
  const isFollowing = user ? issue.followerIds.includes(user.id) : false;
  // Non-blocking — recomputed live off whatever's already fetched, so it stays accurate if a
  // predecessor's own dates shift later, not just at the moment this issue's Start Date is set.
  const startDateWarnings = issue.startDate
    ? checkStartDateAgainstDependencies(issue.startDate, issue.links, allIssues)
    : [];
  // §9d, blocking (unlike the dependency warnings above) — a sub-issue is bounded by
  // `parentIssue`'s schedule, a top-level issue by the `subIssues` it already has. Reuses the
  // parent/sub-issue lists resolved just above rather than re-deriving them.
  const dateBounds = issue.parentIssueId ? subIssueDateBounds(parentIssue) : parentDateBounds(subIssues);
  // An issue can't hold two links of the same relationship to the same target — exclude issues
  // already linked under the currently-selected Link Type from the picker so a duplicate can't
  // be staged in the first place (the backend also rejects it, for direct API callers).
  const alreadyLinkedIds = new Set(
    issue.links.filter((l) => l.linkType === linkType).map((l) => l.linkedIssueId),
  );

  function patchIssue(data: Partial<IssueInput>, onSuccess?: () => void) {
    updateMutation.mutate(
      { projectId, issueId: currentIssueId, data },
      { onSuccess, onError: (err: Error) => setErrorMessage(err.message) },
    );
  }

  function handleAddWorkLog() {
    addWorkLogMutation.mutate(
      {
        projectId,
        issueId: currentIssueId,
        data: {
          hours: parseFloat(logHours),
          date: logDate,
          description: logDescription ,
        },
      },
      {
        onSuccess: () => {
          setLogHours('');
          setLogDate(new Date().toLocaleDateString('en-CA'));
          setLogDateFieldValid(true);
          setLogDescription('');
        },
        onError: (err: Error) => setErrorMessage(err.message),
      },
    );
  }

  const hoursField = (
    <TextField
      label={
        <LabelWithIcon icon={<AccessTimeIcon fontSize={ICON_SIZE.sm} />} label="Hours" inline />
      }
      type="number"
      size="small"
      inputProps={{ min: 0, max: 24, step: 'any' }}
      value={logHours}
      onChange={cappedHoursChange(setLogHours, 24)}
      onKeyDown={blockNegativeHourKeys}
      sx={{ width: isMobile ? 150 : undefined }}
    />
  );
  const dateField = (
    <DateField
      label={<LabelWithIcon icon={<EventIcon fontSize={ICON_SIZE.sm} />} label="Date" inline />}
      size="small"
      fullWidth={isMobile}
      sx={isMobile ? undefined : { width: { sm: 420, md: 350 } }}
      value={logDate}
      onChange={setLogDate}
      onValidityChange={setLogDateFieldValid}
      invalidMessage="Please enter a valid Date."
      error={Boolean(logDate) && !isEditableWorkLogDate(logDate)}
      helperText={
        Boolean(logDate) && !isEditableWorkLogDate(logDate) ? 'Only current/previous week' : ' '
      }
    />
  );
  const descriptionField = (
    <TextField
      label={
        <LabelWithIcon icon={<NotesIcon fontSize={ICON_SIZE.sm} />} label="Description" inline />
      }
      size="small"
      fullWidth
      value={logDescription}
      onChange={(e) => setLogDescription(e.target.value)}
      error={logDescriptionOverLimit}
      helperText={
        logDescriptionOverLimit
          ? `${logDescription.length}/${WORK_LOG_DESCRIPTION_MAX} — ${logDescription.length - WORK_LOG_DESCRIPTION_MAX} characters over the limit`
          : `${logDescription.length}/${WORK_LOG_DESCRIPTION_MAX}`
      }
    />
  );
  const addWorkLogButton = (
    <Button
      variant="outlined"
      size="small"
      sx={{ height: 40 }}
      disabled={
        !isValidHours(logHours) ||
        !logDate ||
        !logDateFieldValid ||
        !isEditableWorkLogDate(logDate) ||
        logDescriptionOverLimit ||
        Boolean(issue.deletedAt)
      }
      onClick={handleAddWorkLog}
    >
      Add
    </Button>
  );

  return (
    <Dialog
      open
      onClose={onClose}
      fullWidth
      maxWidth="md"
      PaperProps={{ sx: { height: { xs: 720, sm: 850, md: 800 } } }}
    >
      <DialogTitle sx={{ wordBreak: 'break-word', pb: 1 }}>
        <Stack
          direction="row"
          justifyContent="flex-end"
          alignItems="center"
          sx={{ mx: -1, mt: -1 }}
        >
          <IconButton onClick={onClose} size="small" aria-label="Close">
            <CloseIcon />
          </IconButton>
        </Stack>
        <Stack spacing={0.5}>
          {/* Mobile: key + copy/follow share one row, and the title drops to its own full-width
              row below (with its edit pencil stretched to that row's right edge —
              stretchTrigger). Tablet/desktop: key block (key on top, icons below) sits beside
              the title in a single row, as before. */}
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            alignItems={{ xs: 'stretch', sm: 'flex-start' }}
            spacing={1}
          >
            <Stack
              direction={{ xs: 'row', sm: 'column' }}
              alignItems={{ xs: 'center', sm: 'stretch' }}
              spacing={{ xs: 1, sm: 0.25 }}
              flexWrap={{ xs: 'wrap', sm: 'nowrap' }}
              useFlexGap
              sx={{ width: 'auto', flexShrink: 0 }}
            >
              {parentIssue ? (
                <Typography variant="h6" sx={{ py: 0.25, whiteSpace: 'nowrap' }}>
                  <MuiLink
                    component="button"
                    variant="h6"
                    underline="always"
                    onClick={() => setCurrentIssueId(parentIssue.id)}
                    sx={{ background: 'none', border: 'none', p: 0, verticalAlign: 'baseline', color: 'primary.light' }}
                  >
                    {parentIssue.issueKey}
                  </MuiLink>{' '}
                  /{' '}
                  <MuiLink component={RouterLink} to={issueLink(projectId, issue.issueKey)} variant="h6">
                    {issue.issueKey}
                  </MuiLink>
                </Typography>
              ) : (
                <MuiLink
                  component={RouterLink}
                  to={issueLink(projectId, issue.issueKey)}
                  variant="h6"
                  sx={{ py: 0.25, whiteSpace: 'nowrap', flexShrink: 0 }}
                >
                  {issue.issueKey}
                </MuiLink>
              )}
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <CopyLinkButton path={issueLink(projectId, issue.issueKey)} />
                <Tooltip title={isFollowing ? 'Unfollow' : 'Follow'}>
                  <IconButton
                    size="small"
                    onClick={() =>
                      followMutation.mutate({ projectId, issueId: currentIssueId, follow: !isFollowing })
                    }
                    aria-label={isFollowing ? 'Unfollow' : 'Follow'}
                  >
                    {isFollowing ? <VisibilityIcon fontSize="small" /> : <VisibilityOffIcon fontSize="small" />}
                  </IconButton>
                </Tooltip>
                {canDeactivate && (
                  <Tooltip title={issue.deletedAt ? 'Restore issue' : 'Deactivate issue'}>
                    <IconButton
                      size="small"
                      color={issue.deletedAt ? 'success' : 'error'}
                      onClick={() => setConfirmDeactivateOpen(true)}
                      aria-label={issue.deletedAt ? 'Restore issue' : 'Deactivate issue'}
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </Stack>
            </Stack>
            <Box sx={{ flex: { sm: 1 }, minWidth: 200, width: { xs: '100%', sm: 'auto' } }}>
              <InlineEditCell
                value={issue.name}
                onSave={(v) => {
                  if (v.trim()) patchIssue({ name: v.trim() });
                }}
                validate={(v) => v.trim().length > 0}
                textVariant="h6"
                showEditIcon
                maxLines={3}
                stretchTrigger
              />
            </Box>
          </Stack>
        </Stack>
      </DialogTitle>
      <DialogContent
        sx={{
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          px: { xs: 1.5, sm: 3 },
        }}
      >
        <Divider sx={{ flexShrink: 0 }} />
        {issue.deletedAt && (
          <Alert severity="error" sx={{ flexShrink: 0, borderRadius: 0 }}>
            This issue was deactivated{issue.deletedBy ? ` by ${issue.deletedBy.name}` : ''}. It is no longer active — work logs cannot be added.
          </Alert>
        )}
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          flexWrap="wrap"
          useFlexGap
          sx={{ my: 1, flexShrink: 0 }}
        >
          {workflow && (
            <FormControl size="small" sx={{ minWidth: 160, '& .MuiInputBase-root': { height: 32 } }}>
              <InputLabel>
                <LabelWithIcon
                  label="Status"
                  icon={<TrendingUpIcon fontSize={ICON_SIZE.sm} />}
                  inline
                />
              </InputLabel>
              <Select
                value={issue.status}
                label={
                  <LabelWithIcon
                    label="Status"
                    icon={<TrendingUpIcon fontSize={ICON_SIZE.sm} />}
                    inline
                  />
                }
                onChange={(e) => {
                  // §15 — a milestone-trigger issue moving to Done fires that milestone's
                  // downstream event; confirm before it happens rather than firing silently.
                  if (e.target.value === 'Done' && issue.isMilestoneTrigger) {
                    setPendingDoneStep(e.target.value);
                    return;
                  }
                  transitionMutation.mutate(
                    { projectId, issueId: currentIssueId, toStep: e.target.value },
                    { onError: (err: Error) => setErrorMessage(err.message) },
                  );
                }}
              >
                {workflow.steps.map((s) => (
                  <MenuItem key={s.id} value={s.name}>
                    {s.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
            <Chip
              icon={<FlagIcon />}
              label={issue.flagged ? 'Flagged' : 'Flag'}
              size="small"
              color={issue.flagged ? 'error' : 'default'}
              onClick={() =>
                updateMutation.mutate(
                  { projectId, issueId: currentIssueId, data: { flagged: !issue.flagged } },
                  { onError: (err: Error) => setErrorMessage(err.message) },
                )
              }
            />
            <Chip
              icon={<BoltIcon />}
              label="Expedite"
              size="small"
              color={issue.expedited ? 'warning' : 'default'}
              // Label is deliberately static, so colour is the only visual state cue — aria-pressed
              // keeps the on/off state available to screen readers (WCAG 1.4.1).
              aria-pressed={issue.expedited}
              onClick={() =>
                updateMutation.mutate(
                  { projectId, issueId: currentIssueId, data: { expedited: !issue.expedited } },
                  { onError: (err: Error) => setErrorMessage(err.message) },
                )
              }
            />
          </Stack>
        </Stack>
        <Tabs
          value={tab}
          onChange={(_e, v: (typeof TABS)[number]) => setTab(v)}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{
            flexShrink: 0,
            borderBottom: 1,
            borderColor: 'divider',
            minHeight: 40,
            '& .MuiTab-root': { minHeight: 40, py: 0.5 },
            '& .MuiTabScrollButton-root.Mui-disabled': { opacity: 0.3 },
            '& .MuiTabs-scrollButtons': { display: { xs: 'flex', md: 'none' } },
          }}
        >
          {visibleTabs.map((t) => (
            <Tab key={t} ref={t === tab ? selectedTabRef : undefined} label={t} value={t} icon={TAB_ICON[t]} iconPosition="start" />
          ))}
        </Tabs>

        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            overflowY: 'auto',
            pt: 0.5,
            pr: 1.5,
          }}
        >
          {tab === 'Details' && (
            <Stack spacing={1}>
              <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 0.5, mt: 0.5 }}>
                {editingDescription ? (
                  <MarkdownField
                    label={
                      <LabelWithIcon
                        icon={<NotesIcon fontSize={ICON_SIZE.sm} />}
                        label="Description"
                        inline
                      />
                    }
                    value={descriptionDraft}
                    onChange={setDescriptionDraft}
                    rows={DESCRIPTION_ROWS}
                    error={descriptionOverLimit}
                    helperText={
                      descriptionOverLimit
                        ? `${descriptionDraft.length}/${DESCRIPTION_MAX} — ${descriptionDraft.length - DESCRIPTION_MAX} characters over the limit`
                        : `${descriptionDraft.length}/${DESCRIPTION_MAX}`
                    }
                    confirmDisabled={descriptionOverLimit}
                    onConfirm={() =>
                      // Explicit `null` (not `undefined`) when cleared — `undefined` is dropped
                      // by the backend's Object.assign-based update (treated as "unchanged"), so
                      // clearing the description never actually saved when this sent `undefined`.
                      // Sending the raw empty string instead persisted the clear but broke the
                      // Audit Log's old -> null "(removed)" rendering, since an empty string
                      // isn't null. `null` gets both right — see IssueInput's description field.
                      patchIssue({ description: descriptionDraft.trim() || null }, () =>
                        setEditingDescription(false),
                      )
                    }
                    onCancel={() => setEditingDescription(false)}
                  />
                ) : (
                  <Box
                    sx={{ cursor: 'pointer' }}
                    onClick={() => {
                      setDescriptionDraft(issue.description ?? '');
                      setEditingDescription(true);
                    }}
                  >
                    <ScrollableBox maxHeight={DESCRIPTION_HEIGHT} sx={{ minHeight: DESCRIPTION_HEIGHT }}>
                      <MarkdownField
                        label={
                          // The edit icon lives inside the label row itself (not a separately
                          // absolutely-positioned overlay) so it inherits the row's own sticky
                          // positioning — pinned to the same spot regardless of scroll, instead
                          // of needing its own z-index/offset that can end up fighting the
                          // sticky row's opaque background for the same corner.
                          <Stack direction="row" justifyContent="space-between" alignItems="center">
                            <LabelWithIcon
                              icon={<NotesIcon fontSize={ICON_SIZE.sm} />}
                              label="Description"
                              inline
                            />
                            <IconButton
                              size="small"
                              aria-label="Edit description"
                              onClick={(e) => {
                                e.stopPropagation();
                                setDescriptionDraft(issue.description ?? '');
                                setEditingDescription(true);
                              }}
                              sx={{ flexShrink: 0, p: 0.25 }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Stack>
                        }
                        value={issue.description ?? ''}
                        onChange={() => {}}
                        readOnly
                        stickyLabel
                      />
                    </ScrollableBox>
                  </Box>
                )}
              </Box>
              <Stack spacing={2} sx={{ p: 1, border: 1, borderColor: 'divider', borderRadius: 1 }}>
                {/* Reporter, Owner, Type, Priority, Start Date, Due Date, Completion Date,
                    Estimate, and Assignees share one Grid so 3-per-row wrapping is continuous at
                    tablet/desktop, in that exact DOM order (no `order` override needed there).
                    Below `sm` (phones), explicit `order` re-pairs the first four as
                    Reporter+Type on one line and Owner+Priority on the next in a 2-column
                    layout, with the remaining fields following in their natural sequence.
                    Module+Sprint and Change Request+Assignees each share one row on tablet/
                    desktop (`sm/md: 6` each) — full width only on phone (`xs: 12` for Module/
                    Sprint/Change Request; Assignees keeps its existing `xs: 6` pairing), where
                    each still gets its own row exactly as before. On tablet/desktop, Module/
                    Sprint/Change Request use a 2-line clamp rather than truncating to one line,
                    with a Tooltip revealing the full name only once it's actually long enough to
                    overflow past 2 lines; phone keeps wrapping the full name with no clamp at
                    all (unchanged) since there's much more vertical room to spend there. Their
                    InlineMenuCell dropdowns are `searchable` at every breakpoint, since these
                    three lists (unlike Status/Priority/Type) can get long enough to need
                    filtering rather than scanning. */}
                <Grid container columnSpacing={2} rowSpacing={1}>
                  <Grid size={{ xs: 6, sm: 4, md: 3 }} sx={{ order: { xs: 1 } }}>
                    <Typography component="div" variant="caption" color="text.secondary">
                      <LabelWithIcon
                        label="Reporter"
                        icon={<PersonIcon sx={{ fontSize: ICON_SIZE.xxxs }} />}
                      />
                    </Typography>
                    <Stack direction="row" alignItems="center" spacing={0.75} sx={{ py: 0.25, px: 0.5 }}>
                      <UserAvatar name={issue.reporter.name} src={issue.reporter.avatarUrl} size={UserAvatar.small} />
                      <Typography variant="body2" color="text.secondary">
                        {issue.reporter.name}
                      </Typography>
                    </Stack>
                  </Grid>
                  <Grid size={{ xs: 6, sm: 4, md: 3 }} sx={{ order: { xs: 3 } }}>
                    <Typography component="div" variant="caption" color="text.secondary">
                      <LabelWithIcon
                        label="Owner"
                        icon={<PersonIcon sx={{ fontSize: ICON_SIZE.xxxs }} />}
                      />
                    </Typography>
                    <CompactEntityPicker
                      label="owner"
                      options={users}
                      value={issue.owner ?? null}
                      onChange={(u) => patchIssue({ ownerId: u?.id ?? null })}
                      trigger={
                        <ButtonBase
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.75,
                            textAlign: 'left',
                            borderRadius: 1,
                            px: 0.5,
                            fontSize: '0.875rem',
                            lineHeight: 1.5,
                            '&.Mui-focusVisible, &:focus-visible': {
                              outline: '2px solid',
                              outlineColor: 'primary.main',
                              outlineOffset: 1,
                            },
                          }}
                        >
                          {issue.owner && (
                            <UserAvatar name={issue.owner.name} src={issue.owner.avatarUrl} size={UserAvatar.small} />
                          )}
                          <Typography
                            variant="body2"
                            color={issue.owner ? 'text.primary' : 'text.secondary'}
                            sx={{ py: 0.25 }}
                          >
                            {issue.owner?.name ?? '—'}
                          </Typography>
                        </ButtonBase>
                      }
                    />
                  </Grid>
                  <Grid size={{ xs: 6, sm: 4, md: 3 }} sx={{ order: { xs: 2 } }}>
                    <Typography component="div" variant="caption" color="text.secondary">
                      <LabelWithIcon
                        label="Type"
                        icon={<TaskIcon sx={{ fontSize: ICON_SIZE.xxxs }} />}
                      />
                    </Typography>
                    <InlineMenuCell
                      trigger={
                        <Typography variant="body2" sx={{ cursor: 'pointer', py: 0.25 }}>
                          {issue.type}
                        </Typography>
                      }
                      options={ISSUE_TYPES.map((t) => ({ value: t, label: t }))}
                      selected={issue.type}
                      onSelect={(v) => patchIssue({ type: v as IssueType })}
                    />
                  </Grid>
                  <Grid size={{ xs: 6, sm: 4, md: 3 }} sx={{ order: { xs: 4 } }}>
                    <Typography component="div" variant="caption" color="text.secondary">
                      <LabelWithIcon
                        label="Priority"
                        icon={<WarningAmberIcon sx={{ fontSize: ICON_SIZE.xxxs }} />}
                      />
                    </Typography>
                    <InlineMenuCell
                      trigger={
                        <Typography
                          variant="body2"
                          color={issue.priority ? 'text.primary' : 'text.secondary'}
                          sx={{ cursor: 'pointer', py: 0.25 }}
                        >
                          {issue.priority ?? '—'}
                        </Typography>
                      }
                      options={[
                        { value: '', label: 'None' },
                        ...ISSUE_PRIORITIES.map((p) => ({ value: p, label: p })),
                      ]}
                      selected={issue.priority ?? ''}
                      onSelect={(v) =>
                        patchIssue({ priority: (v || null) as IssuePriority | null })
                      }
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 4, md: 3 }} sx={{ order: { xs: 5 } }}>
                    <Typography component="div" variant="caption" color="text.secondary">
                      <LabelWithIcon
                        label="Start Date"
                        icon={<EventIcon sx={{ fontSize: ICON_SIZE.xxxs }} />}
                      />
                    </Typography>
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                      <InlineEditCell
                        value={issue.startDate ?? ''}
                        onSave={(v) => patchIssue({ startDate: v || null })}
                        type="date"
                        width={145}
                        dateBounds={dateInputBounds('Start Date', dateBounds)}
                        validateDate={(v) => dateBoundError('Start Date', v, dateBounds)}
                        onValidationError={setErrorMessage}
                      />
                      {startDateWarnings.length > 0 && (
                        <Tooltip title={startDateWarnings.map((w) => w.message).join(' ')}>
                          <WarningAmberIcon sx={{ fontSize: 16, color: 'warning.main' }} />
                        </Tooltip>
                      )}
                    </Stack>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 4, md: 3 }} sx={{ order: { xs: 6 } }}>
                    <Typography component="div" variant="caption" color="text.secondary">
                      <LabelWithIcon
                        label="Due Date"
                        icon={<EventIcon sx={{ fontSize: ICON_SIZE.xxxs }} />}
                      />
                    </Typography>
                    <InlineEditCell
                      value={issue.dueDate ?? ''}
                      onSave={(v) => patchIssue({ dueDate: v || null })}
                      type="date"
                      width={145}
                      dateBounds={dateInputBounds('Due Date', dateBounds)}
                      validateDate={(v) => dateBoundError('Due Date', v, dateBounds)}
                      onValidationError={setErrorMessage}
                    />
                  </Grid>
                  <Grid size={{ xs: 6, sm: 4, md: 3 }} sx={{ order: { xs: 7 } }}>
                    <Typography component="div" variant="caption" color="text.secondary">
                      <LabelWithIcon
                        label="Completion Date"
                        icon={<EventIcon sx={{ fontSize: ICON_SIZE.xxxs }} />}
                      />
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ py: 0.25, px: 0.5 }}>
                      {issue.completionDate
                        ? formatDdMmYyyy(issue.completionDate.slice(0, 10))
                        : '—'}
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 6, sm: 4, md: 3 }} sx={{ order: { xs: 8 } }}>
                    <Typography component="div" variant="caption" color="text.secondary">
                      <LabelWithIcon
                        label="Estimate(h)"
                        icon={<AccessTimeIcon sx={{ fontSize: ICON_SIZE.xxxs }} />}
                      />
                    </Typography>
                    <InlineEditCell
                      value={issue.originalEstimate != null ? String(issue.originalEstimate) : ''}
                      onSave={(v) => patchIssue({ originalEstimate: v === '' ? null : Number(v) })}
                      type="number"
                      width={80}
                      validate={(v) => v === '' || isValidHours(v)}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6, md: 6 }} sx={{ order: { xs: 9 } }}>
                    <Typography component="div" variant="caption" color="text.secondary">
                      <LabelWithIcon
                        label="Module"
                        icon={<ViewModuleIcon sx={{ fontSize: ICON_SIZE.xxxs }} />}
                      />
                    </Typography>
                    <CompactEntityPicker
                      label="module"
                      hideAvatar
                      noOptionClamp
                      fullWidth={false}
                      options={assignableModules}
                      value={issue.module ?? null}
                      onChange={(m) => patchIssue({ moduleId: m?.id ?? null })}
                      trigger={
                        <TruncatableText
                          text={issue.module?.name ?? '—'}
                          variant="body2"
                          color={issue.moduleId ? 'text.primary' : 'text.secondary'}
                          placement="top"
                          sx={{
                            cursor: 'pointer',
                            py: 0.25,
                            // Without this, a value with no natural break point (a long name
                            // with no spaces) can't wrap at all and just overflows past the
                            // field's edge instead — LINE_CLAMP_2_SX already carries its own
                            // wordBreak, but the unclamped mobile branch needs it applied
                            // explicitly too.
                            wordBreak: 'break-word',
                            ...(isMobile ? {} : LINE_CLAMP_2_SX),
                          }}
                        />
                      }
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6, md: 6 }} sx={{ order: { xs: 10 } }}>
                    <Typography component="div" variant="caption" color="text.secondary">
                      <LabelWithIcon
                        label="Sprint"
                        icon={<EventRepeatIcon sx={{ fontSize: ICON_SIZE.xxxs }} />}
                      />
                    </Typography>
                    <CompactEntityPicker
                      label="sprint"
                      hideAvatar
                      noOptionClamp
                      fullWidth={false}
                      options={assignableSprints}
                      value={issue.sprint ?? null}
                      onChange={(s) => patchIssue({ sprintId: s?.id ?? null })}
                      trigger={
                        <TruncatableText
                          text={issue.sprint?.name ?? '—'}
                          variant="body2"
                          color={issue.sprintId ? 'text.primary' : 'text.secondary'}
                          placement="top"
                          sx={{
                            cursor: 'pointer',
                            py: 0.25,
                            // Without this, a value with no natural break point (a long name
                            // with no spaces) can't wrap at all and just overflows past the
                            // field's edge instead — LINE_CLAMP_2_SX already carries its own
                            // wordBreak, but the unclamped mobile branch needs it applied
                            // explicitly too.
                            wordBreak: 'break-word',
                            ...(isMobile ? {} : LINE_CLAMP_2_SX),
                          }}
                        />
                      }
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6, md: 6 }} sx={{ order: { xs: 11 } }}>
                    <Typography component="div" variant="caption" color="text.secondary">
                      <LabelWithIcon
                        label="Change Request"
                        icon={<RuleIcon sx={{ fontSize: ICON_SIZE.xxxs }} />}
                      />
                    </Typography>
                    <CompactEntityPicker
                      label="change request"
                      hideAvatar
                      noOptionClamp
                      fullWidth={false}
                      options={changeRequestPickerOptions}
                      value={selectedChangeRequest}
                      onChange={(cr) => patchIssue({ changeRequestId: cr?.id ?? null })}
                      trigger={
                        <TruncatableText
                          text={selectedChangeRequest?.name ?? '—'}
                          variant="body2"
                          color={issue.changeRequestId ? 'text.primary' : 'text.secondary'}
                          placement="top"
                          sx={{
                            cursor: 'pointer',
                            py: 0.25,
                            // Without this, a value with no natural break point (a long name
                            // with no spaces) can't wrap at all and just overflows past the
                            // field's edge instead — LINE_CLAMP_2_SX already carries its own
                            // wordBreak, but the unclamped mobile branch needs it applied
                            // explicitly too.
                            wordBreak: 'break-word',
                            ...(isMobile ? {} : LINE_CLAMP_2_SX),
                          }}
                        />
                      }
                    />
                  </Grid>
                  <Grid size={{ xs: 6, sm: 6, md: 6 }} sx={{ order: { xs: 12 } }}>
                    <Typography component="div" variant="caption" color="text.secondary">
                      <LabelWithIcon
                        label="Assignees"
                        icon={<GroupsIcon sx={{ fontSize: ICON_SIZE.xxxs }} />}
                      />
                    </Typography>
                    <Stack
                      direction="row"
                      sx={{ py: 0.25, '& > *:not(:first-of-type)': { ml: -0.75 } }}
                    >
                      {issue.assignees.length === 0 ? (
                        <Typography variant="body2" color="text.secondary">
                          —
                        </Typography>
                      ) : (
                        <>
                          {issue.assignees.slice(0, 3).map((a) => (
                            <Tooltip key={a.id} title={a.name}>
                              <UserAvatar
                                name={a.name}
                                src={a.avatarUrl}
                                size={UserAvatar.small}
                                sx={{ border: '1.5px solid', borderColor: 'background.paper' }}
                              />
                            </Tooltip>
                          ))}
                          {issue.assignees.length > 3 && (
                            <UserAvatarOverflow
                              count={issue.assignees.length - 3}
                              size={UserAvatar.small}
                              tooltip={issue.assignees
                                .slice(3)
                                .map((a) => a.name)
                                .join(', ')}
                            />
                          )}
                        </>
                      )}
                    </Stack>
                  </Grid>
                </Grid>
              </Stack>
              {canSeeMilestones && (
                <Stack
                  direction={{ xs: 'column', md: 'row' }}
                  spacing={1}
                  alignItems={{ xs: 'flex-start', md: 'center' }}
                  sx={{ px: 1.5, py: 0.5, border: 1, borderColor: 'divider', borderRadius: 1 }}
                >
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={issue.isMilestoneTrigger}
                        disabled={!canEditMilestones}
                        onChange={(e) => {
                          const checked = e.target.checked;
                          updateMutation.mutate(
                            {
                              projectId,
                              issueId: currentIssueId,
                              // Unchecking also clears both milestone slots in the same request —
                              // leaving either dangling would silently reactivate it if this is
                              // ever re-checked without picking new ones (§15).
                              data: checked
                                ? { isMilestoneTrigger: true }
                                : {
                                  isMilestoneTrigger: false,
                                  paymentMilestoneId: null,
                                  fundingMilestoneId: null,
                                },
                            },
                            { onError: (err: Error) => setErrorMessage(err.message) },
                          );
                        }}
                      />
                    }
                    label="Milestone trigger"
                  />
                  {issue.isMilestoneTrigger && (
                    <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
                      <FormControl
                        size="small"
                        sx={{
                          minWidth: 280,
                          maxWidth: 280,
                          '& .MuiInputBase-root': { height: 32 },
                          '& .MuiSelect-select': {
                            display: 'block',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            py: 0,
                            lineHeight: '32px',
                          },
                          '& .MuiInputLabel-root:not(.MuiInputLabel-shrink)': {
                            top: '50%',
                            transform: 'translate(14px, -50%) scale(1)',
                          },
                        }}
                      >
                        <InputLabel>
                          <LabelWithIcon
                            label="Payment Milestone"
                            icon={<DiamondIcon fontSize={ICON_SIZE.sm} />}
                            inline
                          />
                        </InputLabel>
                        <Select
                          fullWidth
                          value={issue.paymentMilestoneId ?? ''}
                          label={
                            <LabelWithIcon
                              label="Payment Milestone"
                              icon={<DiamondIcon fontSize={ICON_SIZE.sm} />}
                              inline
                            />
                          }
                          disabled={!canEditMilestones}
                          onChange={(e) =>
                            updateMutation.mutate(
                              {
                                projectId,
                                issueId: currentIssueId,
                                data: { paymentMilestoneId: e.target.value || null },
                              },
                              { onError: (err: Error) => setErrorMessage(err.message) },
                            )
                          }
                        >
                          <MenuItem value="">
                            <em>None</em>
                          </MenuItem>
                          {milestones
                            .filter((m) => m.type === 'Payment')
                            .map((m) => (
                              <MenuItem key={m.id} value={m.id}>
                                {m.name}
                              </MenuItem>
                            ))}
                        </Select>
                      </FormControl>
                      <FormControl
                        size="small"
                        sx={{
                          minWidth: 280,
                          maxWidth: 280,
                          '& .MuiInputBase-root': { height: 32 },
                          '& .MuiSelect-select': {
                            display: 'block',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            py: 0,
                            lineHeight: '32px',
                          },
                          '& .MuiInputLabel-root:not(.MuiInputLabel-shrink)': {
                            top: '50%',
                            transform: 'translate(14px, -50%) scale(1)',
                          },
                        }}
                      >
                        <InputLabel>
                          <LabelWithIcon
                            label="Funding Milestone"
                            icon={<DiamondIcon fontSize={ICON_SIZE.sm} />}
                            inline
                          />
                        </InputLabel>
                        <Select
                          fullWidth
                          value={issue.fundingMilestoneId ?? ''}
                          label={
                            <LabelWithIcon
                              label="Funding Milestone"
                              icon={<DiamondIcon fontSize={ICON_SIZE.sm} />}
                              inline
                            />
                          }
                          disabled={!canEditMilestones}
                          onChange={(e) =>
                            updateMutation.mutate(
                              {
                                projectId,
                                issueId: currentIssueId,
                                data: { fundingMilestoneId: e.target.value || null },
                              },
                              { onError: (err: Error) => setErrorMessage(err.message) },
                            )
                          }
                        >
                          <MenuItem value="">
                            <em>None</em>
                          </MenuItem>
                          {milestones
                            .filter((m) => m.type === 'Funding')
                            .map((m) => (
                              <MenuItem key={m.id} value={m.id}>
                                {m.name}
                              </MenuItem>
                            ))}
                        </Select>
                      </FormControl>
                    </Stack>
                  )}
                </Stack>
              )}
            </Stack>
          )}

          {tab === 'Sub-Issues' && !issue.parentIssueId && (
            <Stack spacing={1.5}>
              <Stack direction="row" justifyContent="flex-end">
                <Button size="small" variant="outlined" onClick={() => setSubIssueOpen(true)}>
                  Add Sub-Issue
                </Button>
              </Stack>
              {subIssues.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No sub-issues.
                </Typography>
              ) : (
                subIssues.map((s) => (
                  // Key + Status share a row up top (Status pinned to the right, never squashed
                  // by a long Key), Name on its own row below — same shape on every breakpoint.
                  <Stack
                    key={s.id}
                    spacing={0.5}
                    sx={{ p: 1, border: 1, borderColor: 'divider', borderRadius: 1 }}
                  >
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <MuiLink
                        component="button"
                        variant="body2"
                        underline="always"
                        onClick={() => setCurrentIssueId(s.id)}
                        sx={{ background: 'none', border: 'none', p: 0, verticalAlign: 'baseline' }}
                      >
                        {s.issueKey}
                      </MuiLink>
                      <Chip size="small" label={s.status} sx={{ flexShrink: 0 }} />
                    </Stack>
                    <Box sx={{ maxHeight: 130, overflowY: 'auto' }}>
                      <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                        {s.name}
                      </Typography>
                    </Box>
                  </Stack>
                ))
              )}
            </Stack>
          )}

          {tab === 'Links' && (
            <Stack spacing={1.5} sx={{ mt: 1 }}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} flexWrap="wrap" useFlexGap>
                <FormControl size="small" sx={{ minWidth: 140 }}>
                  <InputLabel>
                    <LabelWithIcon
                      label="Link Type"
                      icon={<LinkIcon fontSize={ICON_SIZE.sm} />}
                      inline
                    />
                  </InputLabel>
                  <Select
                    value={linkType}
                    label={
                      <LabelWithIcon
                        label="Link Type"
                        icon={<LinkIcon fontSize={ICON_SIZE.sm} />}
                        inline
                      />
                    }
                    onChange={(e) => {
                      // Switching the relationship can invalidate staged targets already linked
                      // under the new type (see `alreadyLinkedIds`) — clear the staged selection
                      // rather than silently submitting a now-hidden target.
                      setLinkType(e.target.value as IssueLinkType);
                      setLinkTargets([]);
                    }}
                  >
                    {LINK_TYPES.map((t) => (
                      <MenuItem key={t} value={t}>
                        {t}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                {linkType === 'Depends on' && (
                  <FormControl size="small" sx={{ minWidth: 190 }}>
                    <InputLabel>
                      <LabelWithIcon
                        label="Dependency Type"
                        icon={<ArrowRightAltIcon fontSize={ICON_SIZE.sm} />}
                        inline
                      />
                    </InputLabel>
                    <Select
                      value={dependencyType}
                      label={
                        <LabelWithIcon
                          label="Dependency Type"
                          icon={<ArrowRightAltIcon fontSize={ICON_SIZE.sm} />}
                          inline
                        />
                      }
                      onChange={(e) => setDependencyType(e.target.value as IssueDependencyType)}
                    >
                      {DEPENDENCY_TYPES.map((t) => (
                        <MenuItem key={t} value={t}>
                          {DEPENDENCY_TYPE_LABELS[t]}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
                <Box sx={{ flex: 1, minWidth: 200 }}>
                  <MultiSelectPicker<IIssue>
                    label="Issue"
                    labelIcon={
                      <AddTaskIcon
                        sx={{
                          fontSize: ICON_SIZE.xs,
                          verticalAlign: 'middle',
                          mr: 0.5,
                          position: 'relative',
                          top: '-1px',
                        }}
                      />
                    }
                    options={allIssues.filter(
                      (i) => i.id !== issue.id && !alreadyLinkedIds.has(i.id),
                    )}
                    value={linkTargets}
                    onChange={setLinkTargets}
                    getOptionKey={(i) => i.id}
                    getOptionLabel={(i) => `${i.issueKey} — ${i.name}`}
                  />
                </Box>
                <Stack
                  direction={{ xs: 'row' }}
                  justifyContent="flex-end"
                  sx={{ alignSelf: { xs: 'stretch', sm: 'flex-start' }, ml: { sm: 'auto' } }}
                >
                  <Button
                    variant="outlined"
                    size="small"
                    sx={{ height: 40, flexShrink: 0 }}
                    disabled={linkTargets.length === 0}
                    onClick={() => void handleAddLinks()}
                  >
                    Add
                  </Button>
                </Stack>
              </Stack>
              {issue.links.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No links.
                </Typography>
              ) : (
                issue.links.map((l) => {
                  const linkWarning = startDateWarnings.find((w) => w.linkId === l.id);
                  const typeLabel = (
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                      {linkWarning && (
                        <Tooltip title={linkWarning.message}>
                          <WarningAmberIcon sx={{ fontSize: 16, color: 'warning.main' }} />
                        </Tooltip>
                      )}
                      <Typography variant="body2" color="text.secondary">
                        {formatIssueLinkType(l.linkType, l.dependencyType)}
                      </Typography>
                    </Stack>
                  );
                  const issueLabel = (
                    <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                      {l.linkedIssue?.issueKey} — {l.linkedIssue?.name}
                    </Typography>
                  );
                  return isMobile ? (
                    <Stack
                      key={l.id}
                      spacing={0.5}
                      sx={{ p: 1, border: 1, borderColor: 'divider', borderRadius: 1 }}
                    >
                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                        {typeLabel}
                        <Button size="small" color="error" onClick={() => setLinkToRemove(l)} sx={{ flexShrink: 0 }}>
                          Remove
                        </Button>
                      </Stack>
                      {issueLabel}
                    </Stack>
                  ) : (
                    <Grid
                      key={l.id}
                      container
                      spacing={1}
                      alignItems="center"
                      sx={{ p: 1, border: 1, borderColor: 'divider', borderRadius: 1 }}
                    >
                      <Grid size={3}>{typeLabel}</Grid>
                      <Grid size={7} sx={{ minWidth: 0 }}>{issueLabel}</Grid>
                      <Grid size={2} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <Button size="small" color="error" onClick={() => setLinkToRemove(l)}>
                          Remove
                        </Button>
                      </Grid>
                    </Grid>
                  );
                })
              )}
            </Stack>
          )}

          {tab === 'Comments' && (
            <Stack spacing={1.5}>
              <MarkdownField
                label={
                  <LabelWithIcon
                    icon={<ChatBubbleOutlineIcon fontSize={ICON_SIZE.sm} />}
                    label="New Comment"
                    inline
                  />
                }
                value={commentBody}
                onChange={setCommentBody}
                rows={2}
                error={commentOverLimit}
                helperText={
                  commentOverLimit
                    ? `${commentBody.length}/${COMMENT_MAX} — ${commentBody.length - COMMENT_MAX} characters over the limit`
                    : `${commentBody.length}/${COMMENT_MAX}`
                }
              />
              <Stack direction="row" justifyContent="flex-end">
                <Button
                  variant="contained"
                  size="small"
                  disabled={commentBody.trim().length === 0 || commentOverLimit}
                  onClick={() =>
                    addCommentMutation.mutate(
                      { projectId, issueId: currentIssueId, body: commentBody },
                      {
                        onSuccess: () => setCommentBody(''),
                        onError: (err: Error) => setErrorMessage(err.message),
                      },
                    )
                  }
                >
                  Add Comment
                </Button>
              </Stack>
              {comments.map((c) => {
                const authorLabel = (
                  <LabelWithIcon
                    icon={<PersonIcon sx={{ fontSize: ICON_SIZE.xxxs }} />}
                    label={`${c.author.name} · ${formatDdMmYyyyHm(c.createdAt)}`}
                  />
                );
                return (
                  <Stack
                    key={c.id}
                    sx={{ p: 1.5, border: 1, borderColor: 'divider', borderRadius: 1 }}
                    spacing={0.5}
                  >
                    {user?.id === c.author.id ? (
                      <InlineEditableComment
                        body={c.body}
                        authorLabel={authorLabel}
                        onSave={(body, onSuccess) =>
                          updateCommentMutation.mutate(
                            { projectId, issueId: currentIssueId, commentId: c.id, body },
                            { onSuccess, onError: (err: Error) => setErrorMessage(err.message) },
                          )
                        }
                      />
                    ) : (
                      <>
                        <Typography component="div" variant="caption" color="text.secondary">
                          {authorLabel}
                        </Typography>
                        <Box sx={{ maxHeight: 130, overflowY: 'auto' }}>
                          <MarkdownField label="" value={c.body} onChange={() => {}} readOnly />
                        </Box>
                      </>
                    )}
                  </Stack>
                );
              })}
            </Stack>
          )}

          {tab === 'Work Log' && (
            <Stack spacing={1.5} sx={{ mt: 1 }}>
              {isMobile ? (
                <Stack spacing={1}>
                  <Stack direction="row" spacing={1}>
                    {hoursField}
                    {dateField}
                  </Stack>
                  <Stack direction="row" spacing={1}>
                    {descriptionField}
                    {addWorkLogButton}
                  </Stack>
                </Stack>
              ) : (
                <Stack direction="row" spacing={1} alignItems="flex-start">
                  {hoursField}
                  {dateField}
                  {descriptionField}
                  {addWorkLogButton}
                </Stack>
              )}
              {workLogs.map((w) => {
                const canEdit = isEditableWorkLogDate(w.date) && w.author.id === user?.id;
                return (
                  <Stack
                    key={w.id}
                    sx={{ p: 1, border: 1, borderColor: 'divider', borderRadius: 1 }}
                    spacing={0.5}
                  >
                    {editingWorkLogId === w.id ? (
                      <Stack direction="row" spacing={1} alignItems="flex-start">
                        <TextField
                          label={
                            <LabelWithIcon
                              icon={<AccessTimeIcon fontSize={ICON_SIZE.sm} />}
                              label="Hours"
                              inline
                            />
                          }
                          type="number"
                          size="small"
                          inputProps={{ min: 0, max: 24, step: 'any' }}
                          value={editWorkLogHours}
                          onChange={cappedHoursChange(setEditWorkLogHours, 24)}
                          onKeyDown={blockNegativeHourKeys}
                          helperText=" "
                        />
                        <TextField
                          label={
                            <LabelWithIcon
                              icon={<NotesIcon fontSize={ICON_SIZE.sm} />}
                              label="Description"
                              inline
                            />
                          }
                          size="small"
                          fullWidth
                          value={editWorkLogDescription}
                          onChange={(e) => setEditWorkLogDescription(e.target.value)}
                          error={editWorkLogDescriptionOverLimit}
                          helperText={
                            editWorkLogDescriptionOverLimit
                              ? `${editWorkLogDescription.length}/${WORK_LOG_DESCRIPTION_MAX} — ${editWorkLogDescription.length - WORK_LOG_DESCRIPTION_MAX} characters over the limit`
                              : `${editWorkLogDescription.length}/${WORK_LOG_DESCRIPTION_MAX}`
                          }
                        />
                        <Button size="small" onClick={() => setEditingWorkLogId(null)}>
                          Cancel
                        </Button>
                        <Button
                          size="small"
                          variant="contained"
                          disabled={
                            !isValidHours(editWorkLogHours) || editWorkLogDescriptionOverLimit
                          }
                          onClick={() =>
                            updateWorkLogMutation.mutate(
                              {
                                projectId,
                                issueId: currentIssueId,
                                workLogId: w.id,
                                data: {
                                  hours: parseFloat(editWorkLogHours),
                                  description: editWorkLogDescription.trim() || null,
                                },
                              },
                              {
                                onSuccess: () => setEditingWorkLogId(null),
                                onError: (err: Error) => setErrorMessage(err.message),
                              },
                            )
                          }
                        >
                          Save
                        </Button>
                      </Stack>
                    ) : (
                      <Stack spacing={0.5}>
                        <Stack direction="row" flexWrap="wrap" alignItems="center" columnGap={1.5} rowGap={0.5}>
                          <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                            {w.author.name}
                          </Typography>
                          <Chip size="small" label={`${w.hours}h`} />
                          <Typography variant="body2" color="text.secondary">
                            {formatDdMmYyyy(w.date)}
                          </Typography>
                          {canEdit && (
                            <IconButton
                              size="small"
                              sx={{ ml: 'auto' }}
                              onClick={() => {
                                setEditingWorkLogId(w.id);
                                setEditWorkLogHours(String(w.hours));
                                setEditWorkLogDescription(w.description ?? '');
                              }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          )}
                        </Stack>
                        {w.description && (
                          <Box sx={{ maxHeight: 130, overflowY: 'auto' }}>
                            <Typography variant="body2" sx={{ wordBreak: 'break-word', whiteSpace: 'pre-wrap' }}>
                              {w.description}
                            </Typography>
                          </Box>
                        )}
                      </Stack>
                    )}
                  </Stack>
                );
              })}
            </Stack>
          )}

          {tab === 'History' && (
            <Box sx={{ mt: 1 }}>
              <IssueHistoryPanel projectId={projectId} issueId={currentIssueId} />
            </Box>
          )}
        </Box>
      </DialogContent>

      {subIssueOpen && (
        <IssueFormDialog
          projectId={projectId}
          parentIssue={issue}
          onClose={() => setSubIssueOpen(false)}
          onSaved={() => setSubIssueOpen(false)}
          onError={setErrorMessage}
        />
      )}

      <CustomSnackbar
        open={errorMessage !== null || successMessage !== null}
        onClose={() => {
          setErrorMessage(null);
          setSuccessMessage(null);
        }}
        message={errorMessage ?? successMessage}
        severity={errorMessage !== null ? 'error' : 'success'}
        autoHideDuration={4000}
      />

      <RemoveConfirmDialog
        open={linkToRemove !== null}
        title="Remove link?"
        description={`This will remove the "${linkToRemove?.linkType}" link to ${linkToRemove?.linkedIssue?.issueKey}${linkToRemove?.linkedIssue?.name ? ` — ${linkToRemove.linkedIssue.name}` : ''}.`}
        isPending={removeLinkMutation.isPending}
        onCancel={() => setLinkToRemove(null)}
        onConfirm={() =>
          linkToRemove &&
          removeLinkMutation.mutate(
            { projectId, issueId: currentIssueId, linkId: linkToRemove.id },
            {
              onSuccess: () => {
                setLinkToRemove(null);
                setSuccessMessage('Issue link removed successfully.');
              },
              onError: (err: Error) => {
                setErrorMessage(err.message);
                setLinkToRemove(null);
              },
            },
          )
        }
      />

      <RemoveConfirmDialog
        open={confirmDeactivateOpen}
        title={issue.deletedAt ? 'Restore issue?' : 'Deactivate issue?'}
        description={
          issue.deletedAt
            ? `This will restore "${issue.issueKey} — ${issue.name}" and make it visible again.`
            : `This will deactivate "${issue.issueKey} — ${issue.name}". It will no longer appear in the issues list, board or Gantt but will remain in the database for audit purposes.`
        }
        isPending={deactivateMutation.isPending || activateMutation.isPending}
        onCancel={() => setConfirmDeactivateOpen(false)}
        onConfirm={() => {
          if (issue.deletedAt) {
            activateMutation.mutate(
              { projectId, issueId: currentIssueId },
              {
                onSuccess: () => {
                  setConfirmDeactivateOpen(false);
                  setSuccessMessage('Issue restored successfully.');
                  onClose();
                },
                onError: (err: Error) => {
                  setErrorMessage(err.message);
                  setConfirmDeactivateOpen(false);
                },
              },
            );
          } else {
            deactivateMutation.mutate(
              { projectId, issueId: currentIssueId },
              {
                onSuccess: () => {
                  setConfirmDeactivateOpen(false);
                  setSuccessMessage('Issue deactivated successfully.');
                  onClose();
                },
                onError: (err: Error) => {
                  setErrorMessage(err.message);
                  setConfirmDeactivateOpen(false);
                },
              },
            );
          }
        }}
      />

      {pendingDoneStep && (
        <Dialog open onClose={() => setPendingDoneStep(null)} fullWidth maxWidth="xs">
          <DialogTitle>
            Trigger milestone{triggeredMilestoneLabels.length > 1 ? 's' : ''}?
          </DialogTitle>
          <DialogContent>
            This will trigger{' '}
            {triggeredMilestoneLabels.length > 0
              ? triggeredMilestoneLabels.join(' and ')
              : "this issue's milestone"}{' '}
            — are you sure you want to move it to Done?
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setPendingDoneStep(null)}
              disabled={transitionMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              disabled={transitionMutation.isPending}
              onClick={() =>
                transitionMutation.mutate(
                  { projectId, issueId: currentIssueId, toStep: pendingDoneStep },
                  {
                    onSuccess: () => setPendingDoneStep(null),
                    onError: (err: Error) => {
                      setErrorMessage(err.message);
                      setPendingDoneStep(null);
                    },
                  },
                )
              }
            >
              {transitionMutation.isPending ? 'Moving…' : 'Move to Done'}
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </Dialog>
  );
}

function InlineEditableComment({
  body,
  authorLabel,
  onSave,
}: {
  body: string;
  authorLabel: ReactNode;
  onSave: (body: string, onSuccess: () => void) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(body);
  const overLimit = value.length > COMMENT_MAX;
  if (!editing) {
    return (
      <ScrollableBox maxHeight={130}>
        <MarkdownField
          label={
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              {authorLabel}
              <IconButton
                size="small"
                aria-label="Edit comment"
                onClick={() => setEditing(true)}
                sx={{ flexShrink: 0, p: 0.25 }}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </Stack>
          }
          value={body}
          onChange={() => {}}
          readOnly
          stickyLabel
        />
      </ScrollableBox>
    );
  }
  return (
    <Stack spacing={1}>
      <MarkdownField
        label={authorLabel}
        value={value}
        onChange={setValue}
        rows={2}
        error={overLimit}
        helperText={
          overLimit
            ? `${value.length}/${COMMENT_MAX} — ${value.length - COMMENT_MAX} characters over the limit`
            : `${value.length}/${COMMENT_MAX}`
        }
      />
      <Stack direction="row" spacing={1} justifyContent="flex-end">
        <Button
          size="small"
          onClick={() => {
            setEditing(false);
            setValue(body);
          }}
        >
          Cancel
        </Button>
        <Button
          size="small"
          variant="contained"
          disabled={overLimit}
          onClick={() => onSave(value.trim(), () => setEditing(false))}
        >
          Save
        </Button>
      </Stack>
    </Stack>
  );
}
